import { create } from 'zustand';
import api from '@shared/services/api';
import type { User } from '@/types/user';
import type { LoginResponse } from '@/types/api';
import { initEcho, disconnectEcho } from '@app/realtime';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  login: (email: string, password: string) => Promise<LoginResponse>;
  register: (data: Record<string, string>) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  setUser: (user: User) => void;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('heka_token'),
  isAuthenticated: !!localStorage.getItem('heka_token'),
  isLoading: false,

  login: async (email, password) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
      localStorage.setItem('heka_token', data.token);
      set({ user: data.user, token: data.token, isAuthenticated: true });
      initEcho(data.token);
      return data;
    } finally {
      set({ isLoading: false });
    }
  },

  register: async (formData) => {
    set({ isLoading: true });
    try {
      const { data } = await api.post('/auth/register', formData);
      localStorage.setItem('heka_token', data.token);
      set({ user: data.user, token: data.token, isAuthenticated: true });
      initEcho(data.token);
    } finally {
      set({ isLoading: false });
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      disconnectEcho();
      localStorage.removeItem('heka_token');
      set({ user: null, token: null, isAuthenticated: false });
    }
  },

  fetchMe: async () => {
    if (!get().token) return;
    set({ isLoading: true });
    try {
      const { data } = await api.get('/auth/me');
      set({ user: data.user, isAuthenticated: true });
    } catch {
      localStorage.removeItem('heka_token');
      set({ user: null, token: null, isAuthenticated: false });
    } finally {
      set({ isLoading: false });
    }
  },

  setUser: (user) => set({ user }),

  hydrate: () => {
    const token = localStorage.getItem('heka_token');
    if (token) {
      set({ token, isAuthenticated: true });
      initEcho(token);
      get().fetchMe();
    }
  },
}));
